import { useState, useRef, useCallback } from 'react';

interface DraggableListProps {
  items: { key: string; element: React.ReactNode; className?: string; draggable?: boolean }[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  disabled?: boolean;
}

export default function DraggableList({ items, onReorder, disabled }: DraggableListProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    dragRef.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Need a small delay for the drag image to capture
    e.dataTransfer.setData('text/plain', String(index));
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  }, [disabled]);

  const handleDragLeave = useCallback(() => {
    setOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    if (disabled) return;
    e.preventDefault();
    const fromIndex = dragRef.current;
    if (fromIndex !== null && fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }
    setDragIndex(null);
    setOverIndex(null);
    dragRef.current = null;
  }, [onReorder, disabled]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
    dragRef.current = null;
  }, []);

  const handleMoveUp = useCallback((index: number) => {
    if (disabled) return;
    if (index > 0) onReorder(index, index - 1);
  }, [onReorder, disabled]);

  const handleMoveDown = useCallback((index: number) => {
    if (disabled) return;
    if (index < items.length - 1) onReorder(index, index + 1);
  }, [onReorder, items.length, disabled]);

  return (
    <div className="draggable-list">
      {items.map((item, index) => {
        const isDragging = dragIndex === index;
        const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
        const dropAbove = isOver && dragIndex !== null && dragIndex > index;
        const dropBelow = isOver && dragIndex !== null && dragIndex < index;
        const canDrag = !disabled && item.draggable !== false;

        return (
          <div
            key={item.key}
            className={
              'draggable-item' +
              (isDragging ? ' dragging' : '') +
              (dropAbove ? ' drag-over-above' : '') +
              (dropBelow ? ' drag-over-below' : '') +
              (item.className ? ` ${item.className}` : '')
            }
            draggable={canDrag}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div className="drag-handle" title={disabled ? '' : 'Drag to reorder'}>
              ⠿
            </div>
            <div className="drag-fallback-buttons">
              <button
                className="btn-icon"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0 || disabled}
                title="Move up"
              >
                ↑
              </button>
              <button
                className="btn-icon"
                onClick={() => handleMoveDown(index)}
                disabled={index === items.length - 1 || disabled}
                title="Move down"
              >
                ↓
              </button>
            </div>
            <div className="draggable-item-content">
              {item.element}
            </div>
          </div>
        );
      })}
    </div>
  );
}
